import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { useBusiness } from 'providers/context/BusinessProvider';
import { getBusinessInteractionsLogs, getBusinessProfile, getBusinesses, updateBusinessProfile } from './api';
import { BusinessProfileResponse } from './types';
import { queryClient } from 'providers/AllProviders';
import { InteractionLogResponse } from './agent/callLogs/types';

function useBusinessId() {
  const { selectedBusiness } = useBusiness();
  const { businessId } = useParams<{ businessId?: string }>();

  return businessId ? Number(businessId) : selectedBusiness;
}

export function useGetBusinesses(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: BusinessProfileResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: BusinessProfileResponse[]; meta: MetaApiResponse }>({
    queryKey: ['accessibleBusinesses', params],
    queryFn: () => getBusinesses(params),
    ...options
  });
}

export function useGetBusinessProfile(
  params?: Record<string, any>,
  options?: UseQueryOptions<BusinessProfileResponse, Error>
) {
  const id = useBusinessId();
  const { enabled: enabledOption, ...rest } = options ?? {};

  return useQuery<BusinessProfileResponse>({
    queryKey: ['businessProfile', id, params],
    queryFn: () => getBusinessProfile(id!, params),
    enabled: Boolean(id) && (enabledOption ?? true),
    ...rest
  });
}

export function useUpdateBusinessProfile() {
  const id = useBusinessId();

  return useMutation({
    mutationFn: (payload: any) => updateBusinessProfile(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['businessProfile'],
        refetchType: 'active'
      });
    }
  });
}

export function useGetBusinessInteractionsLogs(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: InteractionLogResponse[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: InteractionLogResponse[]; meta: MetaApiResponse }>({
    queryKey: ['business-interactions', params],
    queryFn: () => getBusinessInteractionsLogs(params),
    ...options
  });
}
